import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "Index funds are baskets that hold every stock in a market or sector — instant diversification at near-zero cost. Individual stocks are a single bet on one company that can outperform or fail. For most beginners, an index fund (or ETF) is the better foundation: lower risk, lower cost, less time. Individual stocks can be added later, once you can clearly explain why you own each one.";

const FAQS = [
  { q: 'Is an ETF the same as an index fund?', a: 'Almost. An index fund is a fund that tracks an index. ETFs (exchange-traded funds) are funds that trade on a stock exchange like a stock. Most popular ETFs are also index funds (VOO, VTI, SPY).' },
  { q: 'Why do index funds outperform most stock pickers?', a: 'Two reasons: lower fees and the math of averages. By definition, the average actively managed fund is the market — minus fees. The S&P 500 has beaten 80%+ of active US equity funds over 15-year periods.' },
  { q: 'Can I make more money picking individual stocks?', a: 'Yes — and you can lose much more. The dispersion is huge. Most individual stock pickers underperform the index after fees and time. A small minority dramatically outperform.' },
  { q: 'How much of my portfolio should be individual stocks?', a: 'A common rule: keep "satellite" individual stock positions under 10–20% of total portfolio. The "core" stays in low-cost index funds. This caps the damage of any single bad pick.' },
  { q: 'Are sector ETFs a middle ground?', a: 'Yes. ETFs like XLK (tech), XLE (energy), or VHT (healthcare) let you take a sector view without picking individual companies. Riskier than the S&P 500, less risky than a single stock.' },
];

export default function IndexFundsVsStocksPage() {
  return (
    <BlogArticleLayout
      slug="index-funds-vs-stocks"
      title="Index Funds vs Individual Stocks: Which Is Better for Beginners?"
      description="Index funds and individual stocks solve different problems. Learn when to use each and how most beginners should mix them."
      keywords="index funds vs stocks, ETF vs individual stocks, should I buy stocks or ETFs, index fund investing, individual stocks for beginners, S&P 500 ETF"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['how-to-start-investing-with-100-dollars', 'how-to-invest', 'investing-mistakes-beginners-make']}
    >
      <section>
        <h2>What is an index fund?</h2>
        <p>
          An index fund is a fund that tries to mirror a stock market index — like the S&P 500 (largest 500 US companies) or the FTSE 100 (largest 100 UK companies).
          Instead of a manager picking which stocks to own, the fund just holds every constituent of the index in proportion.
        </p>
        <p>
          The result is automatic diversification, very low fees (often 0.03–0.10% per year), and performance that nearly matches the market.
        </p>
      </section>

      <section>
        <h2>What does buying individual stocks really mean?</h2>
        <p>
          Buying an individual stock is a focused bet that <em>this</em> company will outperform the average. Done well, it can dramatically beat an index — early Amazon, Tesla, or NVIDIA holders did exactly that.
          Done poorly — which is more common — it underperforms or loses money entirely.
        </p>
        <p>
          For a structured framework, see our guide on <Link to="/blog/stock-picking">stock picking</Link>.
        </p>
      </section>

      <section>
        <h2>How do they compare on the things that actually matter?</h2>
        <ul>
          <li><strong>Diversification.</strong> Index fund: 500–10,000 stocks instantly. Individual stocks: only what you buy.</li>
          <li><strong>Time required.</strong> Index fund: minutes per year. Individual stocks: hours per company per quarter.</li>
          <li><strong>Fees.</strong> Index fund: 0.03–0.10% annually. Individual stocks: $0 commission, but tax drag from trading.</li>
          <li><strong>Expected return.</strong> Index fund: roughly the market average. Individual stocks: very wide range, on average lower than the market for non-experts.</li>
          <li><strong>Risk.</strong> Index fund: market-level volatility. Individual stocks: can be 2–5x more volatile.</li>
        </ul>
      </section>

      <section>
        <h2>Which one should you start with?</h2>
        <p>
          For most beginners, the answer is index funds first, individual stocks later (and only if you want to). Reasons:
        </p>
        <ul>
          <li>You learn the habit of investing without exposing yourself to single-company risk.</li>
          <li>You see what the "average" return feels like, which calibrates expectations for any individual stock you later add.</li>
          <li>You don't need to spend hours researching to start. The barrier is essentially zero.</li>
        </ul>
        <p>
          See our guide on <Link to="/blog/how-to-start-investing-with-100-dollars">starting with $100</Link> for a step-by-step setup.
        </p>
      </section>

      <section>
        <h2>How can you mix the two?</h2>
        <p>
          A common allocation:
        </p>
        <ul>
          <li><strong>80–90%</strong> in a broad-market index fund (VTI, VT, VOO).</li>
          <li><strong>10–20%</strong> in 3–8 individual stocks you genuinely understand.</li>
        </ul>
        <p>
          This caps the downside of any single bad pick while still letting you participate when you have a real conviction. Pair it with our concept guides — <Link to="/blog/what-is-pe-ratio">P/E ratio</Link>, <Link to="/blog/what-is-market-cap">market cap</Link>, <Link to="/blog/what-is-roe">ROE</Link> — to actually understand what you're buying.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
